import { DefaultItem } from "@directus/sdk";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React, { ReactElement, useContext, useEffect, useState } from "react";

import { AppContext } from "../../context/app-context";
import {
  AssetMetashotsType,
  AssetStatus,
  AssetTypes,
  Collection,
  Asset as GqlAsset,
  LanguageRegion,
  Sort,
} from "../../generated/gql/graphql";
import { AssetListContext } from "../../providers/asset-list-provider";
import { CollectionAssetsContext } from "../../providers/collection-assets-provider";
import { useDirectus } from "../../providers/DirectusProvider";
import AssetSelectionActions from "../../sections/asset/asset-selection-actions";
import AssetStatusFilter from "../../sections/files/filters/asset-status-filter";
import AssetTypeFilter from "../../sections/files/filters/asset-type-filter";
import LanguageRegionFilter from "../../sections/files/filters/language-region-filter";
import MetashotTypeFilter, {
  styles,
} from "../../sections/files/filters/metashot-type-filter";
import Asset from "../../services/asset";
import { MetaphoreLanguageRegions } from "../../types/language-regions";
import CollectionModal from "../collection-modal/collection-modal";

type Props = {
  collection?: Collection;
};

export default function Filter(props: Props): ReactElement {
  const { collection } = props;

  const [openDialog, setOpenDialog] = React.useState(false);
  const [firstItem, setFirstItem] = useState<
    DefaultItem<GqlAsset> | undefined
  >();

  const [language, setLanguage] = useState<MetaphoreLanguageRegions[]>([]);
  const { directus } = useDirectus();
  const { filter, setFilter, orderBy, setOrderBy } = useContext(
    CollectionAssetsContext
  );

  const { getAssetById } = Asset(directus);
  const { sorting, setSorting } = useContext(AppContext);

  const { selectAll, clearSelection, selectedAllAssets, selectedFiles } =
    useContext(AssetListContext);

  useEffect(() => {
    (async () => {
      if (selectedFiles[0]) {
        setFirstItem(
          (await getAssetById(selectedFiles[0].id || "-1")) ?? undefined
        );
      }
    })();
  }, [selectedFiles]);

  const handleSortChange = (e: SelectChangeEvent): void => {
    if (e.target.value) {
      setOrderBy({
        [e.target.value]: Sort.Desc,
      });
    } else {
      setOrderBy(undefined);
    }
  };

  function onRegionLanguageSelected(lang: LanguageRegion | "all"): void {
    setFilter({
      ...filter,
      languageRegionId:
        lang === "all"
          ? undefined
          : {
              eq: lang.id,
            },
    });
  }

  function onMetashotSelected(metashotType: AssetMetashotsType | "all"): void {
    setFilter({
      ...filter,
      metashotsType:
        metashotType === "all"
          ? undefined
          : {
              eq: metashotType,
            },
    });
  }

  function onAssetStatusSelected(status: AssetStatus | "all"): void {
    setFilter({
      ...filter,
      status:
        status === "all"
          ? undefined
          : {
              eq: status,
            },
    });
  }

  function onAssetTypeSelected(assetTypes: AssetTypes | "all"): void {
    setFilter({
      ...filter,
      assetTypes:
        assetTypes === "all"
          ? undefined
          : {
              eq: assetTypes,
            },
    });
  }

  function onResetFilters(): void {
    setFilter({});
    setOrderBy({
      date_created: Sort.Asc,
    });
  }

  return (
    <div className="flex w-full mb-[25px] flex-wrap">
      <div className="basis-[60%] flex-wrap flex gap-[15px]">
        <AssetTypeFilter
          value={filter?.assetTypes?.eq as AssetTypes | undefined}
          onSelect={onAssetTypeSelected}
        />
        <AssetStatusFilter
          value={filter?.status?.eq as AssetStatus | undefined}
          onSelect={onAssetStatusSelected}
        />
        {/* <UISelect className="mr-15" label="Creator" name="user_created" list={users} /> */}
        {/* <UISelect className="mr-15" label="File Type" name="file_type" list={getListType(fields, 'file_type')} /> */}
        <MetashotTypeFilter
          className="w-[150px]"
          value={filter?.metashotsType as AssetMetashotsType | undefined}
          onSelect={onMetashotSelected}
        />
        <LanguageRegionFilter
          value={filter?.languageRegionId as LanguageRegion | undefined}
          onSelect={onRegionLanguageSelected}
        />
        {/* <UISelect className="mr-15" label="Rights" name="rights" /> */}
        <button
          onClick={onResetFilters}
          className="hover:underline mt-10 ml-10 inline-block cursor-pointer text-[14px] text-slate-500"
        >
          Reset filter
        </button>
      </div>
      <div className="basis-[40%] text-right flex-wrap flex gap-[15px] items-start justify-end">
        <FormControlLabel
          sx={{
            ".MuiFormControlLabel-label": {
              fontSize: "14px",
              whiteSpace: "nowrap",
            },
          }}
          // onChange={(v) => console.log(v)}
          control={
            <Checkbox
              checked={selectedAllAssets}
              onChange={(v) => {
                v.target.checked ? selectAll() : clearSelection();
              }}
            />
          }
          label="Select All"
        />
        <AssetSelectionActions
          targetAssets={selectedFiles}
          variant={collection ? "collection_assets" : "assets"}
          collection={collection}
        />
        <Box sx={styles}>
          <FormControl style={{ minWidth: 150 }} size="small">
            <InputLabel className="text-[14px]">Sort by</InputLabel>
            <Select
              className="border-0 rounded-[15px] text-[14px] text-left"
              label="Sort by"
              value={orderBy ? Object.keys(orderBy)[0] : ""}
              onChange={handleSortChange}
            >
              <MenuItem value="date_created">Default</MenuItem>
              {/* <MenuItem value="oder_index">Order Index</MenuItem> */}
              <MenuItem value="name">Name</MenuItem>
              {/* <MenuItem value="category">Category</MenuItem> */}
            </Select>
          </FormControl>
        </Box>
      </div>
      {firstItem && (
        <CollectionModal
          openModal={openDialog}
          closeModal={setOpenDialog}
          selectedAssets={selectedFiles}
        />
      )}
    </div>
  );
}
